/**
 * MacroPicker — Dialog that shows the user's macro templates in a tree.
 * Clicking a macro inserts its text into the report editor.
 * Also supports creating, editing, and deleting personal macros.
 */
import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Description,
  Folder,
  FolderOpen,
  Search,
  Edit,
  Delete,
  Add,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import type { MacroBean } from '../../api/types';
import { useAuth } from '../auth/useAuth';

interface MacroPickerProps {
  open: boolean;
  siteKey?: string;
  onClose: () => void;
  onInsert: (text: string) => void;
}

/** Recursive macro tree node */
const MacroNode = ({
  macro,
  depth,
  searchTerm,
  onSelect,
  manageable,
  onEdit,
  onDelete,
}: {
  macro: MacroBean;
  depth: number;
  searchTerm: string;
  onSelect: (text: string) => void;
  manageable?: boolean;
  onEdit?: (macro: MacroBean) => void;
  onDelete?: (macro: MacroBean) => void;
}) => {
  const [expanded, setExpanded] = useState(depth === 0);
  const hasChildren = macro.children && macro.children.length > 0;
  const isFolder = hasChildren && !macro.text;
  const matchesSearch =
    !searchTerm ||
    macro.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    macro.descr?.toLowerCase().includes(searchTerm.toLowerCase());

  // If searching and this node doesn't match and has no matching children, hide
  if (searchTerm && !matchesSearch && !hasMatchingDescendant(macro, searchTerm)) {
    return null;
  }

  return (
    <>
      <ListItemButton
        sx={{ pl: 2 + depth * 2 }}
        onClick={() => {
          if (isFolder) {
            setExpanded(!expanded);
          } else if (macro.text) {
            onSelect(macro.text);
          }
        }}
        selected={false}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          {isFolder ? (
            expanded ? (
              <FolderOpen fontSize="small" color="primary" />
            ) : (
              <Folder fontSize="small" color="primary" />
            )
          ) : (
            <Description fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText
          primary={macro.name || '(unnamed)'}
          secondary={macro.descr}
          primaryTypographyProps={{ fontSize: 13 }}
          secondaryTypographyProps={{ fontSize: 11 }}
        />
        {manageable && !isFolder && (
          <Stack direction="row" spacing={0} sx={{ ml: 1 }}>
            <Tooltip title="Edit macro">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onEdit?.(macro); }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete macro">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onDelete?.(macro); }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        {hasChildren && (expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
      </ListItemButton>
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {macro.children!.map((child) => (
              <MacroNode
                key={child.objKey}
                macro={child}
                depth={depth + 1}
                searchTerm={searchTerm}
                onSelect={onSelect}
                manageable={manageable}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

/** Check if any descendant matches the search term */
function hasMatchingDescendant(macro: MacroBean, term: string): boolean {
  if (!macro.children) return false;
  return macro.children.some(
    (child) =>
      child.name?.toLowerCase().includes(term.toLowerCase()) ||
      child.descr?.toLowerCase().includes(term.toLowerCase()) ||
      hasMatchingDescendant(child, term),
  );
}

export const MacroPicker = ({ open, siteKey, onClose, onInsert }: MacroPickerProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMacro, setEditingMacro] = useState<MacroBean | null>(null);
  const [macroName, setMacroName] = useState('');
  const [macroDescr, setMacroDescr] = useState('');
  const [macroText, setMacroText] = useState('');

  const {
    data: macrosResult,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['macros', siteKey ?? ''],
    queryFn: () => api.getMacros(siteKey),
    enabled: open,
    staleTime: 60_000,
  });

  const macros = macrosResult?.records ?? [];

  // ── Save macro mutation ──
  const saveMacroMutation = useMutation({
    mutationFn: async (params: { macro: MacroBean; isDel?: boolean }) => {
      if (!user?.objKey) throw new Error('Not authenticated');
      return api.saveMacros({
        userObjKey: user.objKey,
        macros: [params.macro],
        isDel: params.isDel,
        needReplace: !params.isDel,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['macros'] });
      setEditDialogOpen(false);
      setEditingMacro(null);
    },
  });

  const handleSelect = useCallback(
    (text: string) => {
      onInsert(text);
      setSearchTerm('');
    },
    [onInsert],
  );

  const handleNewMacro = useCallback(() => {
    setEditingMacro(null);
    setMacroName('');
    setMacroDescr('');
    setMacroText('');
    setEditDialogOpen(true);
  }, []);

  const handleEditMacro = useCallback((macro: MacroBean) => {
    setEditingMacro(macro);
    setMacroName(macro.name || '');
    setMacroDescr(macro.descr || '');
    setMacroText(macro.text || '');
    setEditDialogOpen(true);
  }, []);

  const handleDeleteMacro = useCallback(
    (macro: MacroBean) => {
      if (!confirm(`Delete macro "${macro.name}"?`)) return;
      saveMacroMutation.mutate({ macro, isDel: true });
    },
    [saveMacroMutation],
  );

  const handleSaveMacro = useCallback(() => {
    const macro: MacroBean = {
      objKey: editingMacro?.objKey ?? '',
      name: macroName,
      descr: macroDescr,
      text: macroText,
      owner: user?.objKey,
    };
    saveMacroMutation.mutate({ macro, isDel: false });
  }, [editingMacro, macroName, macroDescr, macroText, user?.objKey, saveMacroMutation]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <span>Insert Macro Template</span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={handleNewMacro}
            >
              New Macro
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 2, pt: 1, pb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search macros…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {error && (
            <Typography color="error" sx={{ p: 2 }}>
              Failed to load macros: {String(error)}
            </Typography>
          )}
          {!isLoading && macros.length === 0 && (
            <Typography sx={{ p: 2, color: 'text.secondary' }}>
              No macros available.
            </Typography>
          )}
          <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
            {macros.map((macro) => (
              <MacroNode
                key={macro.objKey}
                macro={macro}
                depth={0}
                searchTerm={searchTerm}
                onSelect={handleSelect}
                manageable
                onEdit={handleEditMacro}
                onDelete={handleDeleteMacro}
              />
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit/New Macro Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingMacro ? 'Edit Macro' : 'New Macro'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={macroName}
              onChange={(e) => setMacroName(e.target.value)}
              autoFocus
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              value={macroDescr}
              onChange={(e) => setMacroDescr(e.target.value)}
            />
            <TextField
              label="Macro Text"
              size="small"
              fullWidth
              multiline
              minRows={6}
              value={macroText}
              onChange={(e) => setMacroText(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveMacro}
            disabled={!macroName.trim() || !macroText.trim() || saveMacroMutation.isPending}
          >
            {saveMacroMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
